/* eslint-disable react/jsx-filename-extension */
import {
  B1,
  getTranslatedText,
  Loader,
  withErrorBoundary,
  SecondaryButton
} from '@nile/components';
import Loading from '@nile/components/src/Svg/AnimatedSvg/Loading.svg';
import {
  AccountClient,
  GET_IN_APP_NOTIFICATIONS,
  MARK_NOTIFICATIONS_AS_READ
} from '@nile/graphql/src';
import { SVGIcon } from '@superops/super-icons';
import { Heading } from '@superops/ui-design-system';
import React, { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Tooltip } from 'react-clone-tippy';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import Card, { NOTIFICATION_STATE, NOTIFICATION_TYPES } from './Card';
import NoUpdatesSvg from './notification-no-data.svg';
import Tabs, { TABS } from './Tabs';
import { useIntersectionObserver } from './useIntersectionObserver';

type TabType = 'ALL' | 'TICKET' | 'QUOTE' | 'INVOICE';

const Wrapper = styled.div`
  padding: 25px 0 10px 0;
  height: 100vh;
  width: 600px;
`;

const NoDataWrapper = styled.div`
  height: 100%;
  width: 100%;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const HeaderSection = styled.div`
  display: flex;
  padding-right: 10px;
  margin-bottom: 20px;
  h2 {
    display: flex;
    align-items: center;
    margin-left: 20px;
    font-size: 24px;
    font-weight: 700;
  }
`;

const MarkAsReadWrapper = styled.div`
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: #e2ecff;
  margin-left: auto;
  display: grid;
  place-items: center;
  cursor: ${(props) =>
    props.disabled || !props.isVisible ? 'none' : 'pointer'};
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.disabled ? '0.3' : '1')};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'all')};
`;

const TabWrapper = styled.div`
  padding: 10px 10px 0 10px;
  height: 100%;
`;

const RenderIntersection = styled.div`
  height: 10px;
  opacity: 0;
`;

const LoaderWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  height: 400px;
  justify-content: center;
  svg {
    height: 80px;
    width: 80px;
  }
`;

export function MarkAsReadButton({
  onClick,
  isVisible = true,
  disabled
}: {
  onClick: () => void;
  // eslint-disable-next-line react/require-default-props
  isVisible?: boolean;
  disabled: boolean;
}) {
  const intl = useIntl();
  return (
    <MarkAsReadWrapper
      onClick={onClick}
      isVisible={isVisible}
      disabled={disabled}
    >
      <Tooltip
        title={getTranslatedText(intl, 'notifications.markAsRead')}
        position="bottom"
        delay={300}
        trigger="mouseenter"
        animation="shift"
        animateFill={false}
      >
        <SVGIcon name="OpenEnvelope" />
      </Tooltip>
    </MarkAsReadWrapper>
  );
}
const BATCH_COUNT = 20;

function Notifications(props) {
  const [notifications, setNotifications] = useState([]);
  const [listInfo, setListInfo] = useState({});
  const [markAsReadLoading, setMarkAsReadLoading] = useState(false);

  function onCompleted({ getInAppNotifications }) {
    setNotifications((n) => [...n, ...getInAppNotifications.notifications]);
    setListInfo(getInAppNotifications.listInfo);
  }

  const { loading: notificationsLoading, refetch, networkStatus } = useQuery(
    GET_IN_APP_NOTIFICATIONS,
    {
      variables: {
        listInfo: {
          pageSize: BATCH_COUNT
        }
      },
      client: AccountClient,
      onCompleted,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only'
    }
  );

  const [mutateNotificationsAsRead] = useMutation(MARK_NOTIFICATIONS_AS_READ, {
    client: AccountClient
  });

  const [currentTab, setCurrentTab] = useState('ALL');

  function markAsRead(
    notificationIds: Array<{ notificationId: string }>,
    state: 'READ' | 'UNREAD',
    isAll?: boolean
  ) {
    mutateNotificationsAsRead({
      variables: {
        input: {
          state,
          ...(isAll
            ? { allNotifications: true }
            : {
                notificationIds
              })
        }
      }
    })
      .then(({ data: { updateInAppNotificationState } }) => {
        if (updateInAppNotificationState) {
          if (isAll) {
            setNotifications((currNotifications) =>
              currNotifications.map((n) => ({
                ...n,
                state: NOTIFICATION_STATE.READ
              }))
            );
            return;
          }

          setNotifications((currNotifications) =>
            currNotifications.map((n) => {
              if (
                notificationIds.some(
                  ({ notificationId }) => notificationId === n.notificationId
                )
              ) {
                return {
                  ...n,
                  state
                };
              }

              return n;
            })
          );
        }
      })
      .finally(() => {
        setMarkAsReadLoading(null);
      });
  }

  const getFetchCondition = useCallback((tabId) => {
    if (tabId === 'ALL') {
      return {
        pageSize: BATCH_COUNT,
        sort: [
          {
            attribute: 'time',
            order: 'DESC'
          }
        ]
      };
    }
    if (tabId === 'TASK') {
      return {
        pageSize: BATCH_COUNT,
        sort: [
          {
            attribute: 'time',
            order: 'DESC'
          }
        ],
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'event',
              operator: 'includes',
              value: ['45', '46', '47']
            }
          ]
        }
      };
    }

    if (tabId === 'ALERT') {
      return {
        pageSize: BATCH_COUNT,
        sort: [
          {
            attribute: 'time',
            order: 'DESC'
          }
        ],
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'event',
              operator: 'includes',
              value: ['49']
            }
          ]
        }
      };
    }

    return {
      pageSize: BATCH_COUNT,
      sort: [
        {
          attribute: 'time',
          order: 'DESC'
        }
      ],
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'workItem.module',
            operator: 'is',
            value: tabId
          }
        ]
      }
    };
  }, []);

  const fetchMore = useCallback(() => {
    // console.log('ref');
    // @ts-expect-error
    if (listInfo.hasMore) {
      refetch({
        listInfo: {
          ...getFetchCondition(currentTab),
          cursor: {
            attribute: 'time',
            type: 'BEFORE',
            value: notifications.length
              ? notifications[notifications.length - 1].time
              : undefined
          }
        }
      });
    }
  }, [notifications, currentTab, listInfo]);

  const loading = notificationsLoading || networkStatus < 7;

  const ref = useIntersectionObserver(fetchMore, [loading]);

  const onTabChange = useCallback(
    (tabId) => {
      setNotifications([]);
      setCurrentTab(tabId);
      refetch({
        listInfo: getFetchCondition(tabId)
      });
    },
    [refetch, getFetchCondition]
  );

  function renderChildren() {
    if (notificationsLoading && !notifications.length) {
      return (
        <LoaderWrapper style={{ paddingTop: 50 }}>
          <Loading />
        </LoaderWrapper>
      );
    }

    if (!notifications.length) {
      return (
        <NoDataWrapper className="ignoreOverride">
          <NoUpdatesSvg />
          <B1>
            <FormattedMessage id="notifications.noUpdates" />
          </B1>
        </NoDataWrapper>
      );
    }

    return (
      <>
        {notifications.map((n) => {
          return (
            <Card
              onClick={() => {
                if (n.state === NOTIFICATION_STATE.UNREAD) {
                  markAsRead(
                    [{ notificationId: n.notificationId }],
                    NOTIFICATION_STATE.READ
                  );
                }
              }}
              disabled={
                markAsReadLoading === true ||
                n.notificationId === markAsReadLoading
              }
              time={n.time}
              onMarkAsRead={() => {
                setMarkAsReadLoading(n.notificationId);
                markAsRead(
                  [{ notificationId: n.notificationId }],
                  NOTIFICATION_STATE.READ
                );
              }}
              isRead={n.state === NOTIFICATION_STATE.READ}
              workItem={n.workItem}
              vendorConversation={n.vendorConversation}
              clientConversation={n.clientConversation}
              kbArticleFeedback={n.kbArticleFeedback}
              alert={n.alert}
              mention={n.mention}
              eventType={n.event}
              tabRef={props.tab}
              user={n.user}
              emailAddress={n.emailAddress}
              task={n.task}
              approval={n.approval}
            />
          );
        })}
        {listInfo.totalCount > notifications.length && networkStatus < 7 && (
          <div style={{ height: 150 }}>
            <Loader />
          </div>
        )}
        <RenderIntersection ref={ref} />
      </>
    );
  }

  return (
    <Wrapper>
      <HeaderSection>
        <Heading>
          <FormattedMessage id="common.notifications" />
        </Heading>
        <div className="mlauto">
          <SecondaryButton
            onClick={() => {
              /* Mark All as Read */
              setMarkAsReadLoading(true);
              markAsRead(
                notifications.map((n) => ({
                  notificationId: n.notificationId
                })),
                NOTIFICATION_STATE.READ,
                true
              );
            }}
            disabled={markAsReadLoading === true}
            icon="OpenEnvelope"
            label={<FormattedMessage id="notifications.markAllAsRead" />}
            loading={markAsReadLoading === true}
          />
        </div>
      </HeaderSection>
      <Tabs
        onTabChange={onTabChange}
        tabChildren={<TabWrapper>{renderChildren()}</TabWrapper>}
      />
    </Wrapper>
  );
}

export default withErrorBoundary(Notifications);
